import React from "react";
import { money } from "../../utils/format";

// SGEW 18 Oct (owner decision, final: "Without GST, purchases basis"): the lines of Profit & Loss. The Turnover report
// shows the same lines from the same server figures (App\Libraries\ProfitLoss), so one name always means one figure.
// minus: taken off - "Less: ..." (credit notes, GST on credit notes and purchases); total: a sum line;
// belowZero: what a total below zero is called.
export const INCOME_LINES = [
  { key: "sales", label: "Sales (without GST, shipping included)" },
  { key: "returns", label: "Returns (credit notes, without GST)", minus: true },
  { key: "net_sales", label: "Net sales", total: true },
  { key: "services", label: "Services (delivered / completed, without GST)" },
  { key: "scrap_sales", label: "Scrap sales" },
  { key: "other_income", label: "Other income" },
  { key: "total_income", label: "Total income", total: true },
];

export const EXPENSE_LINES = [
  { key: "purchases", label: "Purchases received (without GST)" },
  { key: "carriage", label: "Carriage" },
  { key: "logistics", label: "Logistics" },
  { key: "salaries", label: "Salaries" },
  { key: "vendor_payments", label: "Vendor / job work" },
  { key: "total_expense", label: "Total expense", total: true },
];

export const GST_LINES = [
  { key: "gst_on_sales", label: "GST charged on sales" },
  { key: "gst_on_services", label: "GST charged on services" },
  { key: "gst_on_returns", label: "GST on credit notes", minus: true },
  { key: "gst_on_purchases", label: "GST paid on purchases", minus: true },
  { key: "gst_payable", label: "GST payable", total: true, belowZero: "GST credit left (more paid than charged)" },
];

export const PROFIT_LABEL = "Profit or loss";

// SGEW 22 Oct (owner: the Turnover report shows sales only - it repeated the whole statement): the income lines that are
// turnover and their total; other income, expense, GST and the profit stay on Profit & Loss
const TURNOVER_KEYS = ["sales", "returns", "net_sales", "services", "scrap_sales"];
export const TURNOVER_LINES = [
  ...INCOME_LINES.filter((l) => TURNOVER_KEYS.includes(l.key)),
  { key: "turnover", label: "Turnover (net sales + services + scrap sales)", total: true },
];
const TURNOVER_NOTE =
  "All amounts are without GST. Sales and services include the shipping charged to customers; each bill's whole-rupee " +
  "round-off is left out. Other income, expenses, GST and the profit are on Profit & Loss.";

export const PL_NOTE =
  "All amounts are without GST - SGEW claims back the GST it pays suppliers. Sales and services include the shipping " +
  "charged to customers; each bill's whole-rupee round-off is left out. Profit or loss = total income minus total expense. " +
  "GST payable is shown apart and is not part of the profit.";

// Owner 18 Oct ("+ - is confusing"): no minus signs on the page - a line taken off reads "Less: ..." and a total
// below zero says what it is (GST credit left) with the amount as a plain figure. The CSV keeps signed numbers.
const Line = ({ line, value }) => {
  const n = Number(value) || 0;
  const flipped = line.total && line.belowZero && n < 0;
  // "Less: returns (...)", "Less: GST paid on purchases" - a word in capitals (GST) keeps them
  const lessOf = (t) => `Less: ${/^[A-Z][a-z]/.test(t) ? t.charAt(0).toLowerCase() + t.slice(1) : t}`;
  const label = flipped ? line.belowZero : line.minus ? lessOf(line.label) : line.label;
  return (
    <div
      className={`flex items-baseline justify-between gap-3 py-1.5 ${
        line.total ? "border-t border-gray-200 mt-1 font-semibold text-gray-900" : "text-gray-700"
      }`}
    >
      <span className="min-w-0">{label}</span>
      <span className="shrink-0 tabular-nums">{money(line.minus || flipped ? Math.abs(n) : n)}</span>
    </div>
  );
};

const Block = ({ title, lines, summary, className = "" }) => (
  <section className={`bg-white rounded-lg shadow p-4 ${className}`}>
    <h3 className="font-semibold text-gray-900 mb-1">{title}</h3>
    {lines.map((line) => (
      <Line key={line.key} line={line} value={summary?.[line.key]} />
    ))}
  </section>
);

/** The statement: income and expense side by side (one under the other on a phone), the result, then the GST apart. */
const ProfitLossStatement = ({ summary }) => {
  const profit = Number(summary?.profit_loss) || 0;
  const gst = Number(summary?.gst_payable) || 0;
  return (
    <div className="font-poppins text-sm">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Block title="Income" lines={INCOME_LINES} summary={summary} />
        <Block title="Expense" lines={EXPENSE_LINES} summary={summary} />
      </div>
      <div
        className={`mt-4 rounded-lg shadow p-4 flex flex-wrap items-baseline justify-between gap-2 ${
          profit >= 0 ? "bg-green-50 text-green-800" : "bg-red-50 text-red-700"
        }`}
      >
        <span className="font-semibold">{profit >= 0 ? "Profit" : "Loss"}</span>
        <span className="text-lg font-semibold tabular-nums">{money(Math.abs(profit))}</span>
      </div>
      <Block title="GST (shown apart, not in the profit)" lines={GST_LINES} summary={summary} className="mt-4" />
      {gst < 0 && (
        <p className="text-xs text-gray-500 mt-1 px-1">
          More GST was paid on purchases than charged in this period: the difference is a credit to set off later.
        </p>
      )}
      <p className="text-xs text-gray-500 mt-3 px-1">{PL_NOTE}</p>
    </div>
  );
};

/** The Turnover report's lines: sales, returns, net sales, services, scrap sales and their total. */
export const TurnoverStatement = ({ summary }) => (
  <div className="font-poppins text-sm">
    <Block title="Turnover" lines={TURNOVER_LINES} summary={summary} />
    <p className="text-xs text-gray-500 mt-3 px-1">{TURNOVER_NOTE}</p>
  </div>
);

export default ProfitLossStatement;
